/**
 * Cycle Count Management Page
 * AS9100D-Compliant Inventory Counting Workflow
 * 
 * Features:
 * - Create cycle counts (by Zone, Location, Part, or ABC class)
 * - Start counts with frozen system quantities
 * - Record physical counts with variance calculation
 * - Resolve variances (Adjust, Recount, No Action)
 * - Automatic NCR creation for significant variances
 * 
 * AS9100D Clauses:
 * - 9.1: Monitoring, Measurement, Analysis, Evaluation
 * - 10.2: Nonconformity and Corrective Action
 * - 7.5: Documented Information
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClipboardList,
  Plus,
  Search,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Play,
  Check,
  X,
  Calendar,
  Hash,
  ChevronRight,
  Info,
  RotateCcw,
  FileEdit,
  Boxes,
  MapPin,
  TrendingUp,
  TrendingDown,
  ArrowRightLeft,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';
import ComplianceTooltip from '../components/ComplianceTooltip';
import { ESignatureDialog, ESignatureBadge } from '../components/ESignatureDialog';
import InventoryLayout from '../components/layouts/InventoryLayout';

// Status badge colors
const COUNT_STATUS_COLORS = {
  SCHEDULED: 'bg-blue-100 text-blue-700 border-blue-200',
  IN_PROGRESS: 'bg-amber-100 text-amber-700 border-amber-200',
  PENDING_REVIEW: 'bg-purple-100 text-purple-700 border-purple-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-gray-100 text-gray-700 border-gray-200',
};

const LINE_STATUS_COLORS = {
  PENDING: 'bg-gray-100 text-gray-700 border-gray-200',
  COUNTED: 'bg-green-100 text-green-700 border-green-200',
  VARIANCE: 'bg-amber-100 text-amber-700 border-amber-200',
  RESOLVED: 'bg-blue-100 text-blue-700 border-blue-200',
  RECOUNTED: 'bg-purple-100 text-purple-700 border-purple-200',
};

const COUNT_TYPE_LABELS = {
  ZONE: 'By Zone',
  LOCATION: 'By Location',
  PART: 'By Part',
  ABC: 'ABC Classification',
};

// Variance reason codes
const VARIANCE_REASON_CODES = [
  { value: 'DAMAGE', label: 'Damage/Scrap Not Recorded' },
  { value: 'MISCOUNT', label: 'Previous Miscount' },
  { value: 'MISPLACE', label: 'Misplaced Material' },
  { value: 'THEFT', label: 'Suspected Theft' },
  { value: 'TRANSACTION', label: 'Unrecorded Transaction' },
  { value: 'DATA_ENTRY', label: 'Data Entry Error' },
  { value: 'OTHER', label: 'Other (Specify)' },
];

/**
 * Cycle Count List Tab
 */
const CountListTab = ({ 
  counts, 
  isLoading, 
  onSelect, 
  onRefresh, 
  onCreateNew,
  statusFilter,
  onStatusFilterChange 
}) => {
  return (
    <div className="space-y-4">
      {/* Header with Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="SCHEDULED">Scheduled</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={onCreateNew} data-testid="create-cycle-count-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Cycle Count
          </Button>
        </div>
      </div>
      
      {/* Count List Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#101957]" />
            </div>
          ) : counts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No cycle counts found</p>
              <Button variant="link" onClick={onCreateNew} className="mt-2">
                Create your first cycle count
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-[140px]">Count #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead className="text-center">Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {counts.map((count) => {
                  const progress = count.total_lines > 0 
                    ? Math.round((count.counted_lines / count.total_lines) * 100) 
                    : 0;
                  
                  return (
                    <TableRow 
                      key={count.count_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => onSelect(count)}
                      data-testid={`count-row-${count.count_id}`}
                    >
                      <TableCell className="font-mono font-medium">
                        {count.count_number}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {COUNT_TYPE_LABELS[count.count_type] || count.count_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {count.description || '-'}
                      </TableCell>
                      <TableCell>
                        {count.scheduled_date ? (
                          <span className="text-sm">
                            {formatIST(count.scheduled_date, { includeTime: false })}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                count.variance_lines > 0 ? 'bg-amber-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">
                            {count.counted_lines}/{count.total_lines}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={COUNT_STATUS_COLORS[count.status]}
                        >
                          {count.status === 'IN_PROGRESS' ? 'In Progress' : count.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Count Detail View
 */
const CountDetailView = ({ 
  count, 
  lines,
  isLoading,
  onBack,
  onStart,
  onRecordCount,
  onResolveVariance,
  onComplete,
  onCancel,
  onRefresh
}) => {
  const [lineFilter, setLineFilter] = useState('all');
  const [selectedLine, setSelectedLine] = useState(null);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  if (!count) return null;
  
  const filteredLines = lines.filter(line => {
    if (lineFilter === 'all') return true;
    return line.status === lineFilter;
  });
  
  const canStart = count.status === 'SCHEDULED';
  const canComplete = count.status === 'IN_PROGRESS' && 
    count.counted_lines === count.total_lines &&
    count.variance_lines === 0;
  const canCancel = count.status !== 'COMPLETED' && count.status !== 'CANCELLED';
  
  const accuracy = count.summary?.accuracy_percentage || 100;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
            Back
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-[#101957]">{count.count_number}</h2>
              <Badge variant="outline" className={COUNT_STATUS_COLORS[count.status]}>
                {count.status === 'IN_PROGRESS' ? 'In Progress' : count.status}
              </Badge>
              <ComplianceTooltip tooltipId="count.count_number" />
            </div>
            <p className="text-sm text-gray-500">
              {COUNT_TYPE_LABELS[count.count_type]} | 
              Scheduled: {formatIST(count.scheduled_date, { includeTime: false })}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          {canStart && (
            <Button onClick={onStart} data-testid="start-count-btn">
              <Play className="w-4 h-4 mr-2" />
              Start Count
            </Button>
          )}
          
          {canComplete && (
            <Button onClick={onComplete} className="bg-green-600 hover:bg-green-700" data-testid="complete-count-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete Count
            </Button>
          )}
          
          {canCancel && (
            <Button variant="outline" onClick={() => setShowCancelDialog(true)}>
              <XCircle className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <ClipboardList className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Lines</p>
              <p className="text-xl font-bold text-[#101957]">{count.total_lines}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Counted</p>
              <p className="text-xl font-bold text-green-600">{count.counted_lines}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Variances</p>
              <p className="text-xl font-bold text-amber-600">{count.variance_lines}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-50">
              <Check className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Resolved</p>
              <p className="text-xl font-bold text-purple-600">{count.resolved_lines}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${accuracy >= 95 ? 'bg-green-50' : accuracy >= 90 ? 'bg-amber-50' : 'bg-red-50'}`}>
              <TrendingUp className={`w-5 h-5 ${accuracy >= 95 ? 'text-green-600' : accuracy >= 90 ? 'text-amber-600' : 'text-red-600'}`} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Accuracy</p>
              <p className={`text-xl font-bold ${accuracy >= 95 ? 'text-green-600' : accuracy >= 90 ? 'text-amber-600' : 'text-red-600'}`}>
                {accuracy.toFixed(1)}%
              </p>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Count Lines */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              Count Lines
              <ComplianceTooltip tooltipId="count.system_quantity" />
            </CardTitle>
            <Select value={lineFilter} onValueChange={setLineFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Lines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lines</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="COUNTED">Counted</SelectItem>
                <SelectItem value="VARIANCE">Variance</SelectItem>
                <SelectItem value="RESOLVED">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {count.status === 'SCHEDULED' ? (
            <div className="text-center py-12 text-gray-500">
              <Play className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Start the count to generate count lines</p>
              <p className="text-sm text-gray-400 mt-1">
                System quantities will be frozen at that point
              </p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#101957]" />
            </div>
          ) : filteredLines.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No lines match the filter</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Location</TableHead>
                  <TableHead>Lot / Part</TableHead>
                  <TableHead className="text-right">System Qty</TableHead>
                  <TableHead className="text-right">Counted Qty</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLines.map((line) => {
                  const variance = line.variance_quantity || 0;
                  const variancePct = line.variance_percentage || 0;
                  
                  return (
                    <TableRow key={line.count_line_id} data-testid={`line-row-${line.count_line_id}`}>
                      <TableCell>
                        <div>
                          <span className="font-mono text-sm">{line.location_code}</span>
                          {line.bln_code && (
                            <span className="text-xs text-gray-400 ml-2">BLN: {line.bln_code}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-mono text-sm">{line.lot_number}</span>
                          {line.hpn && (
                            <p className="text-xs text-gray-500">{line.hpn}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {line.system_quantity.toFixed(2)} {line.uom}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {line.counted_quantity !== null ? (
                          `${line.counted_quantity.toFixed(2)} ${line.uom}`
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {line.counted_quantity !== null ? (
                          <div className={`font-mono ${variance === 0 ? 'text-green-600' : variance > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            <span className="flex items-center justify-end gap-1">
                              {variance > 0 ? <TrendingUp className="w-3 h-3" /> : variance < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                              {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                            </span>
                            <span className="text-xs">
                              ({variancePct >= 0 ? '+' : ''}{variancePct.toFixed(2)}%)
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={LINE_STATUS_COLORS[line.status]}>
                          {line.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {count.status === 'IN_PROGRESS' && line.status === 'PENDING' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedLine(line);
                                setShowRecordDialog(true);
                              }}
                              data-testid={`record-count-btn-${line.count_line_id}`}
                            >
                              <FileEdit className="w-3 h-3 mr-1" />
                              Count
                            </Button>
                          )}
                          {count.status === 'IN_PROGRESS' && line.status === 'VARIANCE' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedLine(line);
                                setShowResolveDialog(true);
                              }}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              data-testid={`resolve-variance-btn-${line.count_line_id}`}
                            >
                              <ArrowRightLeft className="w-3 h-3 mr-1" />
                              Resolve
                            </Button>
                          )}
                          {count.status === 'IN_PROGRESS' && line.status === 'COUNTED' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedLine(line);
                                setShowRecordDialog(true);
                              }}
                            >
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Record Count Dialog */}
      <RecordCountDialog
        open={showRecordDialog}
        onOpenChange={setShowRecordDialog}
        line={selectedLine}
        onSubmit={(qty, notes) => {
          onRecordCount(selectedLine.count_line_id, qty, notes);
          setShowRecordDialog(false);
          setSelectedLine(null);
        }}
      />
      
      {/* Resolve Variance Dialog */}
      <ResolveVarianceDialog
        open={showResolveDialog}
        onOpenChange={setShowResolveDialog}
        line={selectedLine}
        onSubmit={(action, reasonCode, explanation, signature) => {
          onResolveVariance(selectedLine.count_line_id, action, reasonCode, explanation, signature);
          setShowResolveDialog(false);
          setSelectedLine(null);
        }}
      />
      
      {/* Cancel Dialog */}
      <CancelCountDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onSubmit={(reason) => {
          onCancel(reason);
          setShowCancelDialog(false);
        }}
      />
    </div>
  );
};

/**
 * Record Count Dialog
 */
const RecordCountDialog = ({ open, onOpenChange, line, onSubmit }) => {
  const [countedQty, setCountedQty] = useState('');
  const [notes, setNotes] = useState('');
  
  // Reset form when dialog opens with a new line
  const handleOpenChange = (isOpen) => {
    if (isOpen && line) {
      setCountedQty(line.counted_quantity?.toString() || '');
      setNotes('');
    }
    onOpenChange(isOpen);
  };
  
  if (!line) return null;
  
  const handleSubmit = () => {
    const qty = parseFloat(countedQty);
    if (isNaN(qty) || qty < 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    onSubmit(qty, notes);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Count</DialogTitle>
          <DialogDescription>
            Enter the physical count for this item
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Location & Lot Info */}
          <div className="bg-slate-50 p-3 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Location:</span>
              <span className="font-mono">{line.location_code}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Lot:</span>
              <span className="font-mono">{line.lot_number}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">System Qty:</span>
              <span className="font-mono font-medium">{line.system_quantity} {line.uom}</span>
            </div>
          </div>
          
          {/* Count Input */}
          <div className="space-y-2">
            <Label htmlFor="counted_qty">
              Counted Quantity <span className="text-red-500">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="counted_qty"
                type="number"
                step="0.01"
                min="0"
                value={countedQty}
                onChange={(e) => setCountedQty(e.target.value)}
                placeholder="Enter counted quantity"
                className="flex-1"
                data-testid="counted-qty-input"
                autoFocus
              />
              <span className="text-gray-500">{line.uom}</span>
            </div>
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="count_notes">Notes (Optional)</Label>
            <Textarea
              id="count_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any observations during count..."
              rows={2}
            />
          </div>
          
          {/* Variance Preview */}
          {countedQty && !isNaN(parseFloat(countedQty)) && (
            <div className={`p-3 rounded-lg border ${
              parseFloat(countedQty) === line.system_quantity 
                ? 'bg-green-50 border-green-200' 
                : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex justify-between text-sm">
                <span>Variance:</span>
                <span className="font-mono font-medium">
                  {(parseFloat(countedQty) - line.system_quantity).toFixed(2)} {line.uom}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Variance %:</span>
                <span className="font-mono font-medium">
                  {line.system_quantity > 0 
                    ? (((parseFloat(countedQty) - line.system_quantity) / line.system_quantity) * 100).toFixed(2)
                    : '—'
                  }%
                </span>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} data-testid="submit-count-btn">
            <Check className="w-4 h-4 mr-2" />
            Record Count
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Resolve Variance Dialog with E-Signature Support
 * AS9100D 10.2 Compliant
 */
const ResolveVarianceDialog = ({ open, onOpenChange, line, onSubmit }) => {
  const [action, setAction] = useState('ADJUST');
  const [reasonCode, setReasonCode] = useState('');
  const [explanation, setExplanation] = useState('');
  const [showESignature, setShowESignature] = useState(false);
  const [signature, setSignature] = useState(null);
  
  // Reset form when dialog opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setAction('ADJUST');
      setReasonCode('');
      setExplanation('');
      setSignature(null);
    }
    onOpenChange(isOpen);
  };
  
  if (!line) return null;
  
  const variancePct = Math.abs(line.variance_percentage || 0);
  let approvalRequired = null;
  let approvalLevel = null;
  let ncrRequired = false;
  
  if (variancePct >= 10) {
    approvalRequired = 'Inventory Manager';
    approvalLevel = 'MANAGER';
    ncrRequired = true;
  } else if (variancePct >= 5) {
    approvalRequired = 'Inventory Manager';
    approvalLevel = 'MANAGER';
  } else if (variancePct >= 1) {
    approvalRequired = 'Supervisor';
    approvalLevel = 'SUPERVISOR';
  }
  
  const needsESignature = approvalRequired !== null;
  
  const handleSubmit = () => {
    if (!reasonCode) {
      toast.error('Please select a reason code');
      return;
    }
    
    if (variancePct >= 5 && !explanation.trim()) {
      toast.error('Explanation is required for variances ≥5%');
      return;
    }
    
    // If e-signature is required and not yet signed, show signature dialog
    if (needsESignature && !signature) {
      setShowESignature(true);
      return;
    }
    
    // Submit with signature data
    onSubmit(action, reasonCode, explanation, signature);
  };
  
  const handleSignatureConfirm = (signatureData) => {
    setSignature(signatureData);
    setShowESignature(false);
    // Automatically submit after signing
    onSubmit(action, reasonCode, explanation, signatureData);
  };
  
  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Resolve Variance
              <ComplianceTooltip tooltipId="count.variance" />
            </DialogTitle>
            <DialogDescription>
              AS9100D 10.2: Document the resolution for this inventory variance
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Variance Summary */}
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-amber-800">
                  {line.part_number} - {line.part_description}
                </span>
                <Badge variant="outline" className={`text-xs ${
                  variancePct >= 10 ? 'bg-red-100 text-red-700 border-red-200' :
                  variancePct >= 5 ? 'bg-amber-100 text-amber-700 border-amber-200' :
                  variancePct >= 1 ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                  'bg-green-100 text-green-700 border-green-200'
                }`}>
                  {variancePct.toFixed(1)}% variance
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">System Qty:</span>
                  <p className="font-mono font-medium">{line.system_quantity} {line.uom}</p>
                </div>
                <div>
                  <span className="text-gray-500">Counted Qty:</span>
                  <p className="font-mono font-medium">{line.counted_quantity} {line.uom}</p>
                </div>
                <div>
                  <span className="text-gray-500">Variance:</span>
                  <p className={`font-mono font-medium ${line.variance_quantity >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    {line.variance_quantity >= 0 ? '+' : ''}{line.variance_quantity} ({line.variance_percentage?.toFixed(2)}%)
                  </p>
                </div>
              </div>
            </div>
            
            {/* Approval Warning */}
            {(approvalRequired || ncrRequired) && (
              <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-sm">
                <p className="font-medium text-red-700 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Per AS9100D Compliance:
                </p>
                <ul className="mt-1 text-red-600 list-disc list-inside">
                  {approvalRequired && (
                    <li>{approvalRequired} approval required (E-Signature)</li>
                  )}
                  {ncrRequired && <li>NCR will be automatically created</li>}
                </ul>
              </div>
            )}
            
            {/* Signature Badge (if already signed) */}
            {signature && (
              <ESignatureBadge signature={signature} />
            )}
            
            {/* Resolution Action */}
            <div className="space-y-2">
              <Label>Resolution Action <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={action === 'ADJUST' ? 'default' : 'outline'}
                  onClick={() => setAction('ADJUST')}
                  className="justify-start"
                  data-testid="action-adjust"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Adjust
                </Button>
                <Button
                  type="button"
                  variant={action === 'RECOUNT' ? 'default' : 'outline'}
                  onClick={() => setAction('RECOUNT')}
                  className="justify-start"
                  data-testid="action-recount"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Recount
                </Button>
                <Button
                  type="button"
                  variant={action === 'NO_ACTION' ? 'default' : 'outline'}
                  onClick={() => setAction('NO_ACTION')}
                  className="justify-start"
                  data-testid="action-no-action"
                >
                  <X className="w-4 h-4 mr-2" />
                  No Action
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                {action === 'ADJUST' && 'Inventory will be adjusted to match the counted quantity'}
                {action === 'RECOUNT' && 'Line will be reset for another count'}
                {action === 'NO_ACTION' && 'Variance will be documented but no adjustment made'}
              </p>
            </div>
            
            {/* Reason Code */}
            <div className="space-y-2">
              <Label>Reason Code <span className="text-red-500">*</span></Label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger data-testid="reason-code-select">
                  <SelectValue placeholder="Select reason for variance" />
                </SelectTrigger>
                <SelectContent>
                  {VARIANCE_REASON_CODES.map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Explanation */}
            <div className="space-y-2">
              <Label htmlFor="explanation">
                Explanation {variancePct >= 5 && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="explanation"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder="Provide detailed explanation for the variance..."
                rows={3}
                data-testid="explanation-input"
              />
              {variancePct >= 5 && (
                <p className="text-xs text-amber-600">
                  Required for variances ≥5% per AS9100D 10.2
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} data-testid="submit-resolution-btn">
              {needsESignature && !signature ? (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Sign & Confirm
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Resolution
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* E-Signature Dialog */}
      <ESignatureDialog
        open={showESignature}
        onOpenChange={setShowESignature}
        onConfirm={handleSignatureConfirm}
        title="Variance Resolution Approval"
        description="This variance resolution requires your electronic signature for AS9100D 10.2 compliance."
        actionDescription={`Resolving ${variancePct.toFixed(1)}% variance on ${line?.part_number || 'N/A'} with action: ${action}`}
        approvalLevel={approvalLevel}
        ncrRequired={ncrRequired}
        submitLabel="Sign & Approve Resolution"
      >
        {/* Summary within signature dialog */}
        <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg text-sm mb-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-gray-500">Action:</span>
              <p className="font-medium">{action}</p>
            </div>
            <div>
              <span className="text-gray-500">Reason:</span>
              <p className="font-medium">{VARIANCE_REASON_CODES.find(r => r.value === reasonCode)?.label || reasonCode}</p>
            </div>
          </div>
        </div>
      </ESignatureDialog>
    </>
  );
};

/**
 * Cancel Count Dialog
 */
const CancelCountDialog = ({ open, onOpenChange, onSubmit }) => {
  const [reason, setReason] = useState('');
  
  // Reset form when dialog opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) setReason('');
    onOpenChange(isOpen);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel Cycle Count</DialogTitle>
          <DialogDescription>
            Please provide a reason for cancelling this count
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <Label htmlFor="cancel_reason">Reason <span className="text-red-500">*</span></Label>
          <Textarea
            id="cancel_reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for cancellation..."
            rows={3}
            className="mt-2"
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep Count
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => {
              if (!reason.trim()) {
                toast.error('Please provide a reason');
                return;
              }
              onSubmit(reason);
            }}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Cancel Count
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Create Cycle Count Dialog
 */
const CreateCountDialog = ({ open, onOpenChange, warehouses, onSubmit }) => {
  const [formData, setFormData] = useState({
    count_type: 'LOCATION',
    warehouse_id: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    description: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Reset form when dialog opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setFormData({
        count_type: 'LOCATION',
        warehouse_id: warehouses[0]?.warehouse_id || '',
        scheduled_date: new Date().toISOString().split('T')[0],
        description: '',
        notes: ''
      });
    }
    onOpenChange(isOpen);
  };
  
  const handleSubmit = async () => {
    if (!formData.warehouse_id) {
      toast.error('Please select a warehouse');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Cycle Count</DialogTitle>
          <DialogDescription>
            AS9100D 9.1: Schedule a new inventory count
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Count Type */}
          <div className="space-y-2">
            <Label>Count Type <span className="text-red-500">*</span></Label>
            <Select 
              value={formData.count_type} 
              onValueChange={(v) => setFormData({...formData, count_type: v})}
            >
              <SelectTrigger data-testid="count-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LOCATION">By Location (All inventory in warehouse)</SelectItem>
                <SelectItem value="ZONE">By Zone (Specific zones)</SelectItem>
                <SelectItem value="PART">By Part (Specific parts)</SelectItem>
                <SelectItem value="ABC">ABC Classification</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Warehouse */}
          <div className="space-y-2">
            <Label>Warehouse <span className="text-red-500">*</span></Label>
            <Select 
              value={formData.warehouse_id} 
              onValueChange={(v) => setFormData({...formData, warehouse_id: v})}
            >
              <SelectTrigger data-testid="warehouse-select">
                <SelectValue placeholder="Select warehouse" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map(wh => (
                  <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>
                    {wh.code} - {wh.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Scheduled Date */}
          <div className="space-y-2">
            <Label>Scheduled Date</Label>
            <Input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
              data-testid="scheduled-date-input"
            />
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="e.g., Monthly cycle count - Zone A"
              data-testid="description-input"
            />
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional instructions or notes..."
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} data-testid="create-count-submit-btn">
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Create Count
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Cycle Count Page Component
 */
const CycleCountPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [counts, setCounts] = useState([]);
  const [selectedCount, setSelectedCount] = useState(null);
  const [countLines, setCountLines] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Load cycle counts
  const loadCounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get('/api/v1/inventory/v2/cycle-counts', { params });
      setCounts(response.data.items || []);
    } catch (err) {
      console.error('Error loading cycle counts:', err);
      toast.error('Failed to load cycle counts');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);
  
  // Load warehouses
  const loadWarehouses = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/inventory/v2/warehouses');
      setWarehouses(response.data || []);
    } catch (err) {
      console.error('Error loading warehouses:', err);
    }
  }, []);
  
  // Load count lines
  const loadCountLines = useCallback(async (countId) => {
    try {
      const response = await api.get(`/api/v1/inventory/v2/cycle-counts/${countId}/lines`);
      setCountLines(response.data.items || []);
    } catch (err) {
      console.error('Error loading count lines:', err);
      toast.error('Failed to load count lines');
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    loadCounts();
    loadWarehouses();
  }, [loadCounts, loadWarehouses]);
  
  // Handle count selection
  const handleSelectCount = async (count) => {
    setSelectedCount(count);
    setSearchParams({ id: count.count_id });
    
    if (count.status !== 'SCHEDULED') {
      await loadCountLines(count.count_id);
    }
  };
  
  // Handle back navigation
  const handleBack = () => {
    setSelectedCount(null);
    setCountLines([]);
    setSearchParams({});
  };
  
  // Create cycle count
  const handleCreateCount = async (data) => {
    try {
      const response = await api.post('/api/v1/inventory/v2/cycle-counts', data);
      toast.success(`Cycle count ${response.data.count_number} created`);
      loadCounts();
      handleSelectCount(response.data);
    } catch (err) {
      console.error('Error creating cycle count:', err);
      toast.error(err.response?.data?.detail || 'Failed to create cycle count');
      throw err;
    }
  };
  
  // Start cycle count
  const handleStartCount = async () => {
    if (!selectedCount) return;
    
    try {
      const response = await api.post(`/api/v1/inventory/v2/cycle-counts/${selectedCount.count_id}/start`);
      toast.success(`Cycle count started with ${response.data.total_lines} lines`);
      setSelectedCount(response.data);
      await loadCountLines(selectedCount.count_id);
    } catch (err) {
      console.error('Error starting count:', err);
      toast.error(err.response?.data?.detail || 'Failed to start count');
    }
  };
  
  // Record count
  const handleRecordCount = async (lineId, quantity, notes) => {
    if (!selectedCount) return;
    
    try {
      await api.put(
        `/api/v1/inventory/v2/cycle-counts/${selectedCount.count_id}/lines/${lineId}`,
        { counted_quantity: quantity, notes }
      );
      toast.success('Count recorded');
      
      // Refresh
      const countResponse = await api.get(`/api/v1/inventory/v2/cycle-counts/${selectedCount.count_id}`);
      setSelectedCount(countResponse.data);
      await loadCountLines(selectedCount.count_id);
    } catch (err) {
      console.error('Error recording count:', err);
      toast.error(err.response?.data?.detail || 'Failed to record count');
    }
  };
  
  // Resolve variance
  const handleResolveVariance = async (lineId, action, reasonCode, explanation, signature = null) => {
    if (!selectedCount) return;
    
    try {
      const payload = { 
        action, 
        reason_code: reasonCode, 
        explanation,
        // Include e-signature data if provided
        e_signature: signature ? {
          signed_by: signature.user_name,
          signed_by_id: signature.user_id,
          signed_at: signature.timestamp,
          acknowledged: signature.acknowledged
        } : null
      };
      
      const response = await api.post(
        `/api/v1/inventory/v2/cycle-counts/${selectedCount.count_id}/lines/${lineId}/resolve`,
        payload
      );
      
      if (response.data.ncr_id) {
        toast.success(`Variance resolved with approval. NCR created: ${response.data.ncr_id}`);
      } else if (signature) {
        toast.success('Variance resolved with e-signature approval');
      } else {
        toast.success('Variance resolved');
      }
      
      // Refresh
      const countResponse = await api.get(`/api/v1/inventory/v2/cycle-counts/${selectedCount.count_id}`);
      setSelectedCount(countResponse.data);
      await loadCountLines(selectedCount.count_id);
    } catch (err) {
      console.error('Error resolving variance:', err);
      toast.error(err.response?.data?.detail || 'Failed to resolve variance');
    }
  };
  
  // Complete count
  const handleCompleteCount = async () => {
    if (!selectedCount) return;
    
    try {
      const response = await api.post(`/api/v1/inventory/v2/cycle-counts/${selectedCount.count_id}/complete`);
      toast.success(`Cycle count completed. Accuracy: ${response.data.summary?.accuracy_percentage?.toFixed(1)}%`);
      setSelectedCount(response.data);
      loadCounts();
    } catch (err) {
      console.error('Error completing count:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete count');
    }
  };
  
  // Cancel count
  const handleCancelCount = async (reason) => {
    if (!selectedCount) return;
    
    try {
      await api.post(`/api/v1/inventory/v2/cycle-counts/${selectedCount.count_id}/cancel`, { reason });
      toast.success('Cycle count cancelled');
      handleBack();
      loadCounts();
    } catch (err) {
      console.error('Error cancelling count:', err);
      toast.error(err.response?.data?.detail || 'Failed to cancel count');
    }
  };
  
  // Refresh current view
  const handleRefresh = async () => {
    if (selectedCount) {
      setIsLoading(true);
      try {
        const response = await api.get(`/api/v1/inventory/v2/cycle-counts/${selectedCount.count_id}`);
        setSelectedCount(response.data);
        await loadCountLines(selectedCount.count_id);
      } finally {
        setIsLoading(false);
      }
    } else {
      loadCounts();
    }
  };
  
  return (
    <InventoryLayout>
      <div className="h-full overflow-auto" data-testid="cycle-count-page">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Page Header */}
          {!selectedCount && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#101957] flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-[#101957]">Cycle Counts</h1>
                  <p className="text-sm text-gray-500">AS9100D 9.1 - Inventory accuracy verification</p>
                </div>
              </div>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1">
                    <Shield className="w-3 h-3" />
                    AS9100D 9.1
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Monitoring, Measurement, Analysis and Evaluation</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        
        {/* Content */}
        {selectedCount ? (
          <CountDetailView
            count={selectedCount}
            lines={countLines}
            isLoading={isLoading}
            onBack={handleBack}
            onStart={handleStartCount}
            onRecordCount={handleRecordCount}
            onResolveVariance={handleResolveVariance}
            onComplete={handleCompleteCount}
            onCancel={handleCancelCount}
            onRefresh={handleRefresh}
          />
        ) : (
          <CountListTab
            counts={counts}
            isLoading={isLoading}
            onSelect={handleSelectCount}
            onRefresh={loadCounts}
            onCreateNew={() => setShowCreateDialog(true)}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        )}
        
        {/* Create Dialog */}
        <CreateCountDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          warehouses={warehouses}
          onSubmit={handleCreateCount}
        />
        </div>
      </div>
    </InventoryLayout>
  );
};

export default CycleCountPage;
